/**
 * InteractiveDesignDialog - Interactive GPON Network Design Tool
 * Allows manual network design with drag & drop, automatic loss calculation, and validation
 */
import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
  ReactFlow,
  Node,
  Edge,
  Controls,
  Background,
  MiniMap,
  useNodesState,
  useEdgesState,
  addEdge,
  Connection,
  ReactFlowProvider,
  Panel,
  useReactFlow,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { DesignOLTNode } from '../NetworkDesign/DesignOLTNode';
import { DesignSplitterNode } from '../NetworkDesign/DesignSplitterNode';
import { DesignONUNode } from '../NetworkDesign/DesignONUNode';
import { DesignPoleNode } from '../NetworkDesign/DesignPoleNode';
import { DesignEdge } from '../NetworkDesign/DesignEdge';
import { generateNetworkNodes } from '../../utils/gponNetworkGenerator';
import { getSplitterOutputLoss } from '../../utils/splitterCatalog';
import { exportFlowToPng } from '../../utils/flowExport';
import './InteractiveDesignDialog.css';

interface InteractiveDesignDialogProps {
  open: boolean;
  onClose: () => void;
}

const nodeTypes = {
  olt: DesignOLTNode,
  splitter: DesignSplitterNode,
  onu: DesignONUNode,
  pole: DesignPoleNode,
};

const edgeTypes = {
  design: DesignEdge,
};

const initialNodes: Node[] = [];
const initialEdges: Edge[] = [];

const InteractiveDesignContent: React.FC<InteractiveDesignDialogProps> = ({ open, onClose }) => {
  const [nodes, setNodes, onNodesChange] = useNodesState<Node>(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>(initialEdges);
  const [selectedTool, setSelectedTool] = useState<'select' | 'olt' | 'splitter' | 'onu' | 'pole'>('select');
  const [nodeCounter, setNodeCounter] = useState({ olt: 0, splitter: 0, onu: 0, pole: 0 });
  const [showONUs, setShowONUs] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const { screenToFlowPosition } = useReactFlow();

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Ignore if user is typing in an input field
      const target = event.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
        return;
      }

      // Delete/Backspace to delete selected nodes/edges
      if (event.key === 'Delete' || event.key === 'Backspace') {
        const selectedNodes = nodes.filter(node => node.selected);
        const selectedEdges = edges.filter(edge => edge.selected);

        if (selectedNodes.length > 0 || selectedEdges.length > 0) {
          event.preventDefault();

          const confirmMessage = [];
          if (selectedNodes.length > 0) {
            confirmMessage.push(`${selectedNodes.length} node(s)`);
          }
          if (selectedEdges.length > 0) {
            confirmMessage.push(`${selectedEdges.length} connection(s)`);
          }

          if (window.confirm(`Delete ${confirmMessage.join(' and ')}?`)) {
            setNodes(nodes.filter(node => !node.selected));
            setEdges(edges.filter(edge => !edge.selected));
          }
        }
      }
    };

    if (open) {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [open, nodes, edges, setNodes, setEdges]);

  // Calculate signal power for all nodes (multi-pass for cascaded splitters)
  const calculateSignalPower = useCallback(() => {
    let updatedNodes = [...nodes];

    // Multi-pass calculation (up to 10 passes for deep cascades)
    for (let pass = 0; pass < 10; pass++) {
      let hasChanges = false;

      updatedNodes = updatedNodes.map(node => {
        if (node.type === 'olt') {
          // OLT has fixed output power
          return node;
        }

        // Find incoming edge
        const incomingEdge = edges.find(edge => edge.target === node.id);
        if (!incomingEdge) {
          // No input connection
          const currentPower = (node.data as any).inputPower;
          if (currentPower !== undefined) {
            hasChanges = true;
            return { ...node, data: { ...node.data, inputPower: undefined } };
          }
          return node;
        }

        // Find source node
        const sourceNode = updatedNodes.find(n => n.id === incomingEdge.source);
        if (!sourceNode) return node;

        // Calculate input power based on source type
        let inputPower: number | undefined;

        if (sourceNode.type === 'olt') {
          // Direct from OLT
          const oltPower = (sourceNode.data as any).power || 3.0;
          const cableLoss = (incomingEdge.data as any)?.loss || 0;
          inputPower = oltPower - cableLoss;
        } else if (sourceNode.type === 'splitter') {
          // From splitter - strata KONKRÉTNEHO výstupu (asymetria), podľa sourceHandle "output-<i>"
          const splitterInputPower = (sourceNode.data as any).inputPower;
          if (splitterInputPower !== undefined) {
            const sourceType = (sourceNode.data as any).type;
            const handle = incomingEdge.sourceHandle || '';
            const m = handle.match(/output-(\d+)/);
            const outIdx = m ? parseInt(m[1], 10) : 0;
            const splitterLoss = getSplitterOutputLoss(sourceType, outIdx);
            const cableLoss = (incomingEdge.data as any)?.loss || 0;
            inputPower = splitterInputPower - splitterLoss - cableLoss;
          }
        }

        // Check if power changed
        const currentPower = (node.data as any).inputPower;
        if (inputPower !== currentPower) {
          hasChanges = true;
        }

        return {
          ...node,
          data: { ...node.data, inputPower },
        };
      });

      // If no changes in this pass, we're done
      if (!hasChanges) break;
    }

    setNodes(updatedNodes);
  }, [nodes, edges, setNodes]);

  // Recalculate signal when nodes or edges change
  useEffect(() => {
    if (nodes.length > 0) {
      calculateSignalPower();
    }
  }, [edges]); // Only on edges change to avoid infinite loop

  // Also recalculate when node data changes (e.g., splitter type change)
  // We use a separate effect with a ref to track previous node data
  const prevNodesDataRef = React.useRef<string>('');
  useEffect(() => {
    // Serialize node data (type, loss) to detect changes
    const nodesDataString = JSON.stringify(
      nodes.map(n => ({
        id: n.id,
        type: n.type,
        loss: (n.data as any)?.loss,
        power: (n.data as any)?.power
      }))
    );

    if (prevNodesDataRef.current !== nodesDataString && prevNodesDataRef.current !== '') {
      console.log('[InteractiveDesign] Node data changed, recalculating signal power');
      calculateSignalPower();
    }

    prevNodesDataRef.current = nodesDataString;
  }, [nodes, calculateSignalPower]);

  // Handle connection between nodes
  const onConnect = useCallback(
    (params: Connection) => {
      const newEdge = {
        ...params,
        type: 'design',
        data: {
          distance: 100, // Default 100m
          loss: 0.03, // 100m * 0.0003 dB/m
          notes: '',
        },
      };
      setEdges((eds) => addEdge(newEdge, eds));
    },
    [setEdges]
  );

  // Add node on canvas click
  const onPaneClick = useCallback(
    (event: React.MouseEvent) => {
      console.log('🎨 Canvas clicked! Selected tool:', selectedTool);

      if (selectedTool === 'select') {
        console.log('⚠️ Select tool active, ignoring click');
        return;
      }

      // Use React Flow's screenToFlowPosition for accurate positioning
      const position = screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });

      console.log('📍 Position calculated:', position);

      const counter = nodeCounter[selectedTool] + 1;
      setNodeCounter({ ...nodeCounter, [selectedTool]: counter });

      const newNode: Node = {
        id: `${selectedTool}-${counter}`,
        type: selectedTool,
        position,
        data: {
          label: `${selectedTool.toUpperCase()} ${counter}`,
          type: selectedTool === 'splitter' ? '1:4' : undefined,
          power: selectedTool === 'olt' ? 3.0 : undefined,
          ports: selectedTool === 'olt' ? 4 : undefined,
          expectedCustomers: selectedTool === 'onu' ? 1 : (selectedTool === 'pole' ? 0 : undefined),
          loss: selectedTool === 'splitter' ? 7.0 : 0,
        },
      };

      console.log('✅ Adding node:', newNode);
      setNodes((nds) => [...nds, newNode]);
      setSelectedTool('select');
    },
    [selectedTool, setNodes, nodeCounter, screenToFlowPosition]
  );

  // Generate network for all poles
  const handleGenerateNetwork = useCallback(() => {
    // Get all pole nodes
    const poleNodes = nodes.filter(n => n.type === 'pole');

    if (poleNodes.length === 0) {
      alert('Žiadne stĺpiky. Najprv nejaké pridajte.');
      return;
    }

    // Generate network
    const result = generateNetworkNodes(poleNodes, nodes, edges);

    // Show warnings if any
    if (result.warnings.length > 0) {
      alert('Warnings:\n' + result.warnings.join('\n'));
    }

    // Add generated nodes and edges
    setNodes([...nodes, ...result.newNodes]);
    setEdges([...edges, ...result.newEdges]);

    // Show success message
    alert(`Generated:\n- ${result.newNodes.filter(n => n.type === 'splitter').length} Splitters\n- ${result.newNodes.filter(n => n.type === 'onu').length} ONUs`);
  }, [nodes, edges, setNodes, setEdges]);

  // Toggle ONU visibility
  const handleToggleONUs = useCallback(() => {
    setShowONUs(!showONUs);
  }, [showONUs]);

  // Filter nodes based on ONU visibility
  const visibleNodes = useMemo(() => {
    if (showONUs) {
      return nodes;
    }
    return nodes.map(node => {
      if (node.type === 'onu') {
        return { ...node, hidden: true };
      }
      return node;
    });
  }, [nodes, showONUs]);

  // Filter edges based on ONU visibility
  const visibleEdges = useMemo(() => {
    if (showONUs) {
      return edges;
    }
    return edges.map(edge => {
      const targetNode = nodes.find(n => n.id === edge.target);
      if (targetNode?.type === 'onu') {
        return { ...edge, hidden: true };
      }
      return edge;
    });
  }, [edges, nodes, showONUs]);

  // Clear all nodes and edges
  const handleClear = useCallback(() => {
    if (window.confirm('Vymazať všetky uzly a prepojenia?')) {
      setNodes([]);
      setEdges([]);
      setNodeCounter({ olt: 0, splitter: 0, onu: 0, pole: 0 });
    }
  }, [setNodes, setEdges]);

  const handleExportImage = useCallback(async () => {
    setIsExporting(true);
    try {
      const filename = `navrh-siete-${new Date().toISOString().split('T')[0]}.png`;
      await exportFlowToPng(visibleNodes, filename);
    } catch (error) {
      console.error('Export failed:', error);
      alert('Export zlyhal. Skúste to znova.');
    } finally {
      setIsExporting(false);
    }
  }, [visibleNodes]);

  if (!open) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      onClick={onClose}
    >
      <div
        className="interactive-design-dialog"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="design-header">
          <h2>🎨 Interaktívny návrh siete</h2>
          <button onClick={onClose} className="close-btn">
            ✕
          </button>
        </div>

        {/* Toolbar */}
        <div className="design-toolbar">
          <button
            className={`tool-btn ${selectedTool === 'select' ? 'active' : ''}`}
            onClick={() => setSelectedTool('select')}
            title="Vybrať a presunúť uzly"
          >
            ↖️ Vybrať
          </button>
          <button
            className={`tool-btn ${selectedTool === 'olt' ? 'active' : ''}`}
            onClick={() => setSelectedTool('olt')}
            title="Pridať OLT (Optical Line Terminal)"
          >
            🏢 OLT
          </button>
          <button
            className={`tool-btn ${selectedTool === 'pole' ? 'active' : ''}`}
            onClick={() => setSelectedTool('pole')}
            title="Pridať stĺpik (so zákazníkmi)"
          >
            🏗️ Stĺpik
          </button>
          <button
            className={`tool-btn ${selectedTool === 'splitter' ? 'active' : ''}`}
            onClick={() => setSelectedTool('splitter')}
            title="Pridať splitter"
          >
            🔀 Splitter
          </button>
          <button
            className={`tool-btn ${selectedTool === 'onu' ? 'active' : ''}`}
            onClick={() => setSelectedTool('onu')}
            title="Pridať ONU (koncový bod zákazníka)"
          >
            📡 ONU
          </button>
          <div className="toolbar-divider" />
          <button
            className="tool-btn"
            onClick={handleGenerateNetwork}
            title="Vygenerovať GPON sieť pre všetky stĺpiky"
            style={{
              background: nodes.filter(n => n.type === 'pole').length > 0 ? '#4caf50' : undefined,
              color: nodes.filter(n => n.type === 'pole').length > 0 ? 'white' : undefined,
            }}
          >
            ⚡ Generovať sieť
          </button>
          <button
            className={`tool-btn ${!showONUs ? 'active' : ''}`}
            onClick={handleToggleONUs}
            title={showONUs ? 'Skryť ONU' : 'Zobraziť ONU'}
          >
            {showONUs ? '👁️ Skryť ONU' : '👁️‍🗨️ Zobraziť ONU'}
          </button>
          <button className="tool-btn" onClick={handleClear} title="Vymazať všetko">
            🗑️ Vymazať
          </button>
          <button className="tool-btn" onClick={handleExportImage} disabled={isExporting} title="Exportovať celý návrh ako obrázok (PNG)">
            {isExporting ? '⏳ Exportujem…' : '🖼️ Export obrázka'}
          </button>
        </div>

        {/* Canvas */}
        <div className="design-canvas">
          <ReactFlow
            nodes={visibleNodes}
            edges={visibleEdges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onPaneClick={onPaneClick}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            fitView
            fitViewOptions={{ padding: 0.2 }}
          >
            <Background />
            <Controls />
            <MiniMap
              nodeColor={(node) => {
                if (node.type === 'olt') return '#2196f3';
                if (node.type === 'pole') return '#9c27b0';
                if (node.type === 'splitter') return '#ff9800';
                if (node.type === 'onu') return '#4caf50';
                return '#999';
              }}
            />
            <Panel position="top-right">
              <div className="design-info">
                <div style={{ fontSize: '12px', marginBottom: '8px', color: '#333' }}>
                  <strong>Network Stats:</strong>
                </div>
                <div style={{ fontSize: '11px', display: 'flex', flexDirection: 'column', gap: '4px', color: '#666' }}>
                  <div>🏢 OLTs: {nodes.filter(n => n.type === 'olt').length}</div>
                  <div>🏗️ Poles: {nodes.filter(n => n.type === 'pole').length}</div>
                  <div>🔀 Splitters: {nodes.filter(n => n.type === 'splitter').length}</div>
                  <div>📡 ONUs: {nodes.filter(n => n.type === 'onu').length} {!showONUs && '(hidden)'}</div>
                  <div>🔗 Cables: {edges.length}</div>
                </div>
              </div>
            </Panel>
            {selectedTool !== 'select' && (
              <Panel position="top-left">
                <div className="tool-hint">
                  Click on canvas to add {selectedTool.toUpperCase()}
                </div>
              </Panel>
            )}
          </ReactFlow>
        </div>
      </div>
    </div>
  );
};

export const InteractiveDesignDialog: React.FC<InteractiveDesignDialogProps> = (props) => {
  return (
    <ReactFlowProvider>
      <InteractiveDesignContent {...props} />
    </ReactFlowProvider>
  );
};

